import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { Theme } from '@logto/schemas';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import { changeLanguage } from '@/i18n/utils';
import ThemeToggle from '@/shared/components/ThemeToggle';

import styles from './TopBar.module.scss';

/**
 * TDA: page-level header for the auth screens.
 *
 * NEW FILE — upstream has no equivalent, so it costs nothing at merge time.
 * The language switcher and the theme toggle, and nothing else.
 *
 * ⚠️ It used to carry the brand as well, hidden by a `:has()` rule on the four
 * screens whose card drew its own copy. That left the brand in the page corner
 * on every other screen — `/unknown-session`, forgot password, verification
 * code, MFA, SSO email — and inside the card on the rest. `Layout/AppLayout`
 * now draws it once for all of them, so there is nothing to hide and no rule to
 * keep in step.
 */

/** Only the locales TDA ships. See packages/phrases-experience/src/index.ts. */
const languages = [
  { tag: 'vi-VN', label: 'VI' },
  { tag: 'ja', label: 'JP' },
  { tag: 'en', label: 'EN' },
] as const;

const TopBar = () => {
  const { theme, setTheme } = useContext(PageContext);
  const { i18n } = useTranslation();
  const current = i18n.language;

  return (
    <header className={styles.topBar}>
      <div className={styles.actions}>
        <nav className={styles.languages}>
        {languages.map(({ tag, label }) => (
          <button
            key={tag}
            type="button"
            // i18next resolves `vi-VN` to `vi-VN`, but a browser may report
            // plain `vi`; compare on the prefix so the right chip lights up.
            className={current.split('-')[0] === tag.split('-')[0] ? styles.active : undefined}
            onClick={() => {
              void changeLanguage(tag);
            }}
          >
            {label}
          </button>
        ))}
        </nav>
        {/* Shared with the Account Center — one switch, one storage key. */}
        <ThemeToggle className={styles.themeToggle} theme={theme} onChange={setTheme} />
      </div>
    </header>
  );
};

export default TopBar;
